import { createContext, useContext, useMemo } from 'react';

import type { Client } from 'typesense';

import { createSearchClient } from '#src/app/pages/search/shared/api';
import type { SearchClient } from '#src/app/pages/search/shared/api';

type SearchClientContextValue = {
	searchClient: SearchClient;
	collectionName: string;
	decisionMakersCollectionName: string;
};

const SearchClientContext = createContext<SearchClientContextValue | null>(null);

type SearchClientProviderProps = {
	typesenseClient: Client;
	collectionName: string;
	preset: string;
	decisionMakersCollectionName: string;
	children: React.ReactNode;
};

/**
 * Creates the Typesense-backed search factory once and provides it. Filter state and results live
 * in the URL and react-query respectively (see useSearchState / useSearchResults) — this provider
 * only owns the client/factory lifecycle.
 */
export function SearchClientProvider({
	typesenseClient,
	collectionName,
	preset,
	decisionMakersCollectionName,
	children,
}: SearchClientProviderProps) {
	const value = useMemo(
		() => ({
			searchClient: createSearchClient(typesenseClient, { collectionName, preset, decisionMakersCollectionName }),
			collectionName,
			decisionMakersCollectionName,
		}),
		[typesenseClient, collectionName, preset, decisionMakersCollectionName],
	);

	return <SearchClientContext.Provider value={value}>{children}</SearchClientContext.Provider>;
}

export function useSearchClientContext(): SearchClientContextValue {
	const ctx = useContext(SearchClientContext);
	if (!ctx) throw new Error('useSearchClientContext must be used within SearchClientProvider');
	return ctx;
}
